'use client';

import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import LogoInfo from '../logoInfo';
import MuxPlayer from '@mux/mux-player-react';
import { useGlobalData } from '@/lib/api';

interface MediaItem {
  url: string;
}

interface AboutTBC {
  address: string;
  logo: { url: string };
  title: string;
  description: string;
}

interface MuxVideo {
  playback_id: string;
  __typename?: string;
}

interface HeroSectionProps {
  data: {
    heading: string;
    media?: {
      mobile_media?: MediaItem[];
      desktop_media?: MediaItem[];
      mux_video?: MuxVideo;
      enable_mux_video: boolean;
    };
    padding: {
      mobile_top_padding: any;
      mobile_bottom_padding: any;
      desktop_top_padding: any;
      desktop_bottom_padding: any;
    };
    about_tbc: AboutTBC;
  };
}

const ComponentSectionsHomeHero = ({ data }: HeroSectionProps) => {
  const { global } = useGlobalData();

  const mobileBg = data?.media?.mobile_media?.[0]?.url || '/default-mobile.jpg';
  const desktopBg = data?.media?.desktop_media?.[0]?.url || '/default-desktop.jpg';
  const muxPlaybackId = data?.media?.mux_video?.playback_id;
  const enableMuxVideo = data?.media?.enable_mux_video;
  const [top, setTop] = useState('');
  const [bottom, setBottom] = useState('');

  useEffect(() => {
    const updatePadding = () => {
      const isMobile = window.innerWidth <= 768;
      if (isMobile) {
        setTop(data.padding?.mobile_top_padding ?? '');
        setBottom(data.padding?.mobile_bottom_padding ?? '');
      } else {
        setTop(data.padding?.desktop_top_padding ?? '');
        setBottom(data.padding?.desktop_bottom_padding ?? '');
      }
    };

    updatePadding(); // Run on mount

    window.addEventListener('resize', updatePadding); // Listen on resize
    return () => window.removeEventListener('resize', updatePadding); // Clean up
  }, [data]);
  return (
    <section
      className="relative h-screen bg-[#2a3329] flex items-center justify-center overflow-hidden"
      style={{
        paddingTop: `${top}rem`,
        paddingBottom: `${bottom}rem`,
      }}
    >
      {/* Video or Image Background */}
      <div
        className="absolute inset-0 w-full h-full z-0 overflow-hidden pointer-events-none"
        style={{
          transition: 'transform 0.1s ease-out',
        }}
      >
        {enableMuxVideo && muxPlaybackId ? (
          <MuxPlayer
            playbackId={muxPlaybackId}
            autoPlay
            muted
            loop
            playsInline
            streamType="on-demand"
            className="h-full"
            style={{ width: '100%' }}
          />
        ) : (
          <>
            {/* Mobile Fallback Image */}
            <div
              className="sm:hidden absolute inset-0 bg-cover bg-center"
              style={{ backgroundImage: `url('${mobileBg}')` }}
            />
            {/* Desktop Fallback Image */}
            <div
              className="hidden sm:block absolute inset-0 bg-cover bg-center"
              style={{ backgroundImage: `url('${desktopBg}')` }}
            />
          </>
        )}
      </div>

      {/* Content */}
      <motion.div
        className="relative z-10 flex flex-col items-center text-center px-4"
        initial={{ opacity: 0, y: -40 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
      >
        {global?.site_logo?.url && (
          // eslint-disable-next-line @next/next/no-img-element
          <img
            className="object-contain w-full max-h-[100px] mb-4 site-logocenter"
            src={global.site_logo.url}
            alt={global.site_name || 'Site Logo'}
            style={{ maxWidth: '541px', width: '100vw' }}
          />
        )}
      </motion.div>

      {/* About TBC Info */}
      <LogoInfo fillColor="#E5E1D5" />
    </section>
  );
};

export default ComponentSectionsHomeHero;
